import { useEffect, useMemo, useState } from 'react'
import BackOfficeAPIClient from '../clients/BackofficeAPIClient'
import { BrandOption } from '../types/BrandOption'
import { SiteOption } from '../types/SiteOption'
import { SiteBrand } from '../types/SiteBrand'

const backofficeApiClient = new BackOfficeAPIClient()

export interface BrandsData {
    brands: BrandOption[]
    siteIdsByBrand: Map<string, Set<number>>
    loading: boolean
}

/**
 * Catalogue complet des marques + association marque -> sites, pour le contexte "marque"
 * (select simple de marque, puis select multiple de sites filtré par marque choisie).
 * N'est déclenché que si `enabled` est vrai, c'est-à-dire uniquement quand on est réellement
 * dans ce contexte.
 *
 * Les marques ne sont pas rattachées directement à un site (IDSite) mais à la société
 * (IDPlaque) à laquelle il appartient — cf. Societes/Form.js:fetchMarquesComp et
 * Sites/Form.js:fetchSiteMarques, qui interrogent toutes deux /api/societes/:IDPlaque/marques
 * (une requête par société). Ici, comme il faut TOUTES les sociétés d'un coup, on passe par
 * /api/societes/marques?idPlaques=... (une seule requête, cf. SocieteController.get_marques_of_societes).
 */
export function useBrands(sites: SiteOption[], enabled: boolean): BrandsData {
    const [ associations, setAssociations ] = useState<SiteBrand[]>([])
    // Initialisé sur `enabled` (pas juste false) : sinon il y a une fenêtre d'un render, avant que
    // l'effet ci-dessous ne s'exécute, où loading vaut faussement false alors qu'un fetch va bien
    // démarrer — un appelant qui teste `!loading` pour savoir si tout est prêt (cf. FixedFees.tsx)
    // se ferait piéger par ce faux négatif initial.
    const [ loading, setLoading ] = useState(enabled)

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            return
        }

        const idPlaques = Array.from(new Set(sites.map(site => site.idPlaque)))
        if (idPlaques.length === 0) {
            setLoading(false)
            return
        }

        let cancelled = false
        setLoading(true)

        backofficeApiClient.get<SiteBrand[]>(`/societes/marques?idPlaques=${idPlaques.join(',')}`)
            .catch(error => {
                console.log(error)
                return [] as SiteBrand[]
            })
            .then(result => {
                if (!cancelled) {
                    setAssociations(result)
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [ sites, enabled ])

    return useMemo(() => {
        if (associations.length === 0) {
            return { brands: [], siteIdsByBrand: new Map(), loading }
        }

        const siteIdsByBrand = new Map<string, Set<number>>()
        const brandsByPlaque = new Map<number, Set<string>>()
        for (const association of associations) {
            const brandsForPlaque = brandsByPlaque.get(association.IDPlaque) ?? new Set<string>()
            brandsForPlaque.add(association.Make)
            brandsByPlaque.set(association.IDPlaque, brandsForPlaque)
        }

        for (const site of sites) {
            const brandsForSite = brandsByPlaque.get(site.idPlaque) ?? new Set<string>()
            for (const brand of brandsForSite) {
                const siteIds = siteIdsByBrand.get(brand) ?? new Set<number>()
                siteIds.add(site.value)
                siteIdsByBrand.set(brand, siteIds)
            }
        }

        const brands = Array.from(new Set(associations.map(association => association.Make)))
            .sort((a, b) => a.localeCompare(b, 'fr'))
            .map(make => ({ value: make, label: make }))

        return { brands, siteIdsByBrand, loading }
    }, [ associations, sites, loading ])
}
